import { DocumentProviderType } from 'models/documentProvider';
import { DocumentOutputFolderRule } from 'models/documentOutputLocation';
import { EvidenceLocationDTO } from 'models/dto/evidenceLocationDTO';
import {
  FolderNamePreviewDTO,
  FolderNamePreviewRequestDTO,
  FolderNameTokenDTO,
  OutputLocationBindingDTO,
  OutputLocationBindingsDTO,
  OutputLocationDefaultBindingPutDTO,
  OutputLocationTemplateBindingPutDTO,
} from 'models/dto/outputLocationBindingDTO';
import EvidenceLocation, { EvidenceLocationSource } from 'models/evidenceLocation';
import OutputLocationBinding, {
  defaultFolderNameFormat,
  FolderNamePreview,
  FolderNameToken,
  OutputLocationBindings,
  OutputLocationScope,
} from 'models/outputLocationBinding';
import { TaskTypes } from 'models/tasks/task';

export function mapFromFolderNameToken(dto: FolderNameTokenDTO): FolderNameToken {
  const output = new FolderNameToken();

  output.key = dto.key;
  output.formats = dto.formats ?? [];
  output.sample = dto.sample;

  return output;
}

export function mapFromOutputLocationBinding(dto: OutputLocationBindingDTO): OutputLocationBinding {
  const output = new OutputLocationBinding();

  output.bindingId = dto.bindingId;
  output.scope = dto.scope as OutputLocationScope;
  output.templateTaskId = dto.templateTaskId;
  output.templateName = dto.templateName;
  output.templateTaskType = dto.templateTaskType as TaskTypes | undefined;
  output.listId = dto.listId;
  output.listName = dto.listName;
  output.listUrl = dto.listUrl;
  output.providerType = dto.providerType as DocumentProviderType | undefined;
  output.folderRule = dto.folderRule as DocumentOutputFolderRule | undefined;
  output.folderNameFormat = dto.folderNameFormat;
  output.folderDriveItemId = dto.folderDriveItemId;
  output.folderName = dto.folderName;
  output.inherited.list = dto.inherited?.list !== false;
  output.inherited.folderRule = dto.inherited?.folderRule !== false;
  output.inherited.format = dto.inherited?.format !== false;
  output.effective.listId = dto.effective?.listId;
  output.effective.folderDriveItemId = dto.effective?.folderDriveItemId;
  output.effective.folderName = dto.effective?.folderName;
  output.effective.folderRule = (dto.effective?.folderRule ??
    DocumentOutputFolderRule.Flat) as DocumentOutputFolderRule;
  output.effective.folderNameFormat = dto.effective?.folderNameFormat || defaultFolderNameFormat;

  return output;
}

export function mapFromOutputLocationBindings(dto: OutputLocationBindingsDTO): OutputLocationBindings {
  const output = new OutputLocationBindings();

  output.default = mapFromOutputLocationBinding(dto.default ?? new OutputLocationBindingDTO());
  output.templates = (dto.templates ?? []).map((binding) => mapFromOutputLocationBinding(binding));
  output.tokens = (dto.tokens ?? []).map((token) => mapFromFolderNameToken(token));

  return output;
}

export function mapToOutputLocationDefaultBindingPut(
  folderRule: DocumentOutputFolderRule | undefined,
  folderNameFormat: string | undefined,
): OutputLocationDefaultBindingPutDTO {
  const output = new OutputLocationDefaultBindingPutDTO();

  output.folderRule = folderRule ?? null;
  output.folderNameFormat = folderNameFormat ?? null;

  return output;
}

export function mapToOutputLocationTemplateBindingPut(
  listId: number | undefined,
  folderDriveItemId: string | undefined,
  folderName: string | undefined,
  folderRule: DocumentOutputFolderRule | undefined,
  folderNameFormat: string | undefined,
): OutputLocationTemplateBindingPutDTO {
  const output = new OutputLocationTemplateBindingPutDTO();

  output.listId = listId ?? null;
  output.folderDriveItemId = folderDriveItemId ?? null;
  output.folderName = folderDriveItemId ? folderName ?? null : null;
  output.folderRule = folderRule ?? null;
  output.folderNameFormat = folderNameFormat ?? null;

  return output;
}

export function mapToFolderNamePreviewRequest(format: string, sampleTaskId?: number): FolderNamePreviewRequestDTO {
  const output = new FolderNamePreviewRequestDTO();

  output.format = format;
  output.sampleTaskId = sampleTaskId ?? null;

  return output;
}

export function mapFromFolderNamePreview(dto: FolderNamePreviewDTO): FolderNamePreview {
  const output = new FolderNamePreview();

  output.levels = dto.levels ?? [];
  output.problems = dto.problems ?? [];

  return output;
}

export function mapFromEvidenceLocation(dto: EvidenceLocationDTO): EvidenceLocation {
  const output = new EvidenceLocation();

  output.bound = dto.bound === true;
  output.reason = dto.reason;
  output.listId = dto.listId;
  output.listName = dto.listName;
  output.listUrl = dto.listUrl;
  output.providerType = dto.providerType as DocumentProviderType | undefined;
  output.folderRule = dto.folderRule as DocumentOutputFolderRule | undefined;
  output.folderName = dto.folderName;
  output.folderDriveItemId = dto.folderDriveItemId;
  output.folderUrl = dto.folderUrl;
  output.source = dto.source as EvidenceLocationSource | undefined;
  output.templateName = dto.templateName;
  output.bindingId = dto.bindingId;

  return output;
}
